import type { RegistrationFormMetadata } from '@/admin/types/products'
import type { HostAttributeMeta } from '@/admin/types/hosts'
import type { RegistrationField } from '@/admin/hooks/use-registration-fields'
import { addressDisplay, isAddressLike } from '@/shared/lib/formatting'

/**
 * Canonical column → set of attribute key variants we accept when sourcing
 * an address part from `hostAttributeValues`. The wizard formatter looks at
 * an attribute's `code_name`/`maps_to_column` and, if it matches any variant
 * here, copies the value into the canonical key so `addressDisplay` can
 * recognise it.
 */
const ADDRESS_COLUMN_VARIANTS: Record<string, ReadonlySet<string>> = {
  address_line_1: new Set([
    'address_line_1',
    'line_1',
    'addressLine1',
    'street_address',
  ]),
  address_line_2: new Set(['address_line_2', 'line_2', 'addressLine2']),
  address_town_or_city: new Set([
    'address_town_or_city',
    'town_or_city',
    'townOrCity',
    'city',
    'locality',
  ]),
  address_county: new Set(['address_county', 'county', 'region', 'state']),
  address_postcode: new Set([
    'address_postcode',
    'postcode',
    'postalCode',
    'zip_code',
    'zip',
  ]),
  address_country: new Set([
    'address_country',
    'country',
    'countryCode',
  ]),
}

function canonicalAddressColumn(column: string): string | null {
  for (const [canonical, variants] of Object.entries(ADDRESS_COLUMN_VARIANTS)) {
    if (variants.has(column)) return canonical
  }
  return null
}

export interface HostPropertyWizardSlice {
  hostAttributeValues: Record<string, unknown>
  registrationFieldValues: Record<string, unknown>
}

function hostSelectLabel(attr: HostAttributeMeta | undefined, raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return ''
  if (!attr) return String(raw)
  if (attr.field_type === 'SELECT' || attr.field_type === 'MULTI_SELECT') {
    const options = (attr.configuration_data?.options ?? []) as {
      value: string
      label: string
    }[]
    const match = options.find((o) => o.value === String(raw))
    return match?.label ?? String(raw)
  }
  return String(raw)
}

/**
 * Same behaviour as `formatPropertyAddressFromContext` on step-review: prefers
 * ADDRESS-typed host attributes and host-scoped registration ADDRESS fields,
 * then falls back to column-mapped address parts (not raw pipe-delimited keys).
 */
export function formatPropertyAddressFromWizardContext(
  state: HostPropertyWizardSlice,
  metadata: RegistrationFormMetadata | null,
  registrationFields: RegistrationField[] | undefined,
): string {
  const hostAttributes = metadata?.host_template?.attributes ?? []

  for (const attribute of hostAttributes) {
    if (attribute.field_type !== 'ADDRESS') continue
    const formatted = addressDisplay(state.hostAttributeValues[attribute.code_name])
    if (formatted) return formatted
  }

  for (const field of registrationFields ?? []) {
    if (!field.is_active || field.field_type !== 'ADDRESS') continue
    if (
      field.capture_step !== 'host' &&
      field.capture_step !== 'host_context' &&
      field.capture_step !== 'host_product'
    ) {
      continue
    }
    const formatted = addressDisplay(state.registrationFieldValues[field.code_name])
    if (formatted) return formatted
  }

  const addressColumns: Record<string, unknown> = {}
  const recordPart = (canonical: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return
    if (addressColumns[canonical]) return
    addressColumns[canonical] = value
  }

  for (const attribute of hostAttributes) {
    const column = attribute.maps_to_column || attribute.code_name
    const canonical = canonicalAddressColumn(column)
    if (!canonical) continue
    recordPart(canonical, state.hostAttributeValues[attribute.code_name])
  }

  for (const [key, value] of Object.entries(state.hostAttributeValues)) {
    const canonical = canonicalAddressColumn(key)
    if (!canonical) continue
    recordPart(canonical, value)
  }

  const direct = addressDisplay(addressColumns)
  if (direct) return direct

  for (const value of Object.values(state.hostAttributeValues)) {
    if (isAddressLike(value)) {
      const formatted = addressDisplay(value)
      if (formatted) return formatted
    }
    if (typeof value === 'string' && value.trim().startsWith('{')) {
      const formatted = addressDisplay(value)
      if (formatted && formatted !== value.trim()) return formatted
    }
  }

  for (const value of Object.values(state.registrationFieldValues)) {
    if (isAddressLike(value)) {
      const formatted = addressDisplay(value)
      if (formatted) return formatted
    }
  }

  return ''
}

/**
 * Human-readable structure / property-type line from host template metadata
 * (e.g. "Residential (single family detached)").
 */
export function formatPropertyStructureSummaryFromHost(
  state: HostPropertyWizardSlice,
  metadata: RegistrationFormMetadata | null,
): string | null {
  const hostAttributes = metadata?.host_template?.attributes ?? []
  const parts: string[] = []
  const seen = new Set<string>()

  for (const code of ['structure_type', 'other_structure_type'] as const) {
    const attr = hostAttributes.find(
      (a) => a.code_name === code || a.maps_to_column === code,
    )
    if (!attr || seen.has(attr.code_name)) continue
    const label = hostSelectLabel(attr, state.hostAttributeValues[attr.code_name])
    if (label) {
      parts.push(label)
      seen.add(attr.code_name)
    }
  }

  return parts.length > 0 ? parts.join(' - ') : null
}

/**
 * Single line for duplicate modals: formatted street address, optional type.
 */
export function formatPropertyLineForDuplicateModal(
  state: HostPropertyWizardSlice,
  metadata: RegistrationFormMetadata | null,
  registrationFields: RegistrationField[] | undefined,
): string {
  const address = formatPropertyAddressFromWizardContext(state, metadata, registrationFields)
  const structure = formatPropertyStructureSummaryFromHost(state, metadata)
  if (address && structure) return `${address} · ${structure}`
  return address || structure || ''
}
